import { useEffect, useRef, useState, type ComponentProps } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, CalendarOff, CalendarClock } from 'lucide-react'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  listRecurring,
  createRecurring,
  updateRecurring,
  type RecurringItem,
  type RecurringKind,
  type RecurringType,
  type Frequency,
} from '@/api/recurring'
import { listCategories } from '@/api/categories'
import { listAccounts } from '@/api/accounts'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { useReadOnlyKnown } from '@/hooks/useReadOnlyKnown'
import { DatePicker } from '@/components/DatePicker'
import { IconPicker } from '@/components/IconPicker'
import {
  EMPTY_SHARE,
  RecurringShareSection,
  shareError,
  sharePayload,
  type ShareValue,
} from '@/components/recurring/RecurringShareSection'
import { DueThisWeekStrip } from '@/components/recurring/DueThisWeekStrip'
import { RecurringDetail } from '@/components/recurring/RecurringDetail'
import { RecurringHero } from '@/components/recurring/RecurringHero'
import { RecurringRow } from '@/components/recurring/RecurringRow'
import { TabButton } from '@/components/ui/tab-button'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { FREQUENCY_LABELS, KIND_LABELS, KIND_SECTIONS } from '@/lib/recurring'
import { formatDate } from '@/lib/formatDate'
import { BILL_ICON_OPTIONS, BILL_TEMPLATES } from '@/lib/billIconOptions'
import { SUBSCRIPTION_ICON_OPTIONS, SUBSCRIPTION_TEMPLATES } from '@/lib/subscriptionIconOptions'
import { CATEGORY_ICON_OPTIONS } from '@/lib/categoryIconOptions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CategoryIcon } from '@/lib/categoryIcon'

type Tab = 'payments' | 'income' | 'cancelled'

const KINDS: RecurringKind[] = ['BILL', 'EMI', 'SUBSCRIPTION', 'OTHER']

const formSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100),
    type: z.enum(['INCOME', 'EXPENSE']),
    amount: z.coerce.number().positive('Amount must be positive'),
    frequency: z.enum(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'YEARLY']),
    nextDueDate: z.string().min(1, 'Date is required'),
    categoryId: z.string().optional(),
    financialAccountId: z.string().optional(),
    loanAccountId: z.string().optional(),
    notes: z.string().optional(),
    kind: z.enum(['BILL', 'EMI', 'SUBSCRIPTION', 'OTHER']).optional(),
    icon: z.string().optional(),
    amountVaries: z.boolean().optional(),
  })
  .refine((v) => v.type !== 'EXPENSE' || !!v.kind, {
    message: 'Kind is required',
    path: ['kind'],
  })

type FormValues = z.infer<typeof formSchema>

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-[var(--fl-text-2)]">{label}</span>
      <span className="text-[var(--fl-text-1)] text-right">{value}</span>
    </div>
  )
}

// BILL/EMI/SUBSCRIPTION take the first three categorical hues, in that fixed order;
// OTHER always gets the dedicated gray, never a generated 4th hue.
// ── Add / Edit modal ────────────────────────────────────────────────────────

export function RecurringModal({
  initial,
  defaultType,
  defaultFinancialAccountId,
  defaults,
  onClose,
}: {
  initial?: RecurringItem
  defaultType: RecurringType
  defaultFinancialAccountId?: string
  // Pre-fills a new item without locking anything (unlike defaultFinancialAccountId).
  defaults?: {
    name?: string
    amount?: number
    kind?: RecurringKind
    frequency?: Frequency
    loanAccountId?: string
    financialAccountId?: string
  }
  onClose: () => void
}) {
  const { symbol, format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts(),
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues: initial
      ? {
          name: initial.name,
          type: initial.type,
          amount: initial.amount,
          frequency: initial.frequency,
          nextDueDate: initial.nextDueDate,
          categoryId: initial.categoryId ?? '',
          financialAccountId: initial.financialAccountId ?? '',
          notes: initial.notes ?? '',
          kind: initial.kind ?? 'OTHER',
          icon: initial.icon ?? '',
          amountVaries: initial.amountVaries ?? false,
        }
      : {
          type: defaultType,
          frequency: 'MONTHLY',
          kind: 'OTHER',
          ...defaults,
          financialAccountId: defaultFinancialAccountId ?? defaults?.financialAccountId ?? '',
        },
  })
  const type = watch('type')
  const kind = watch('kind')
  const categoryId = watch('categoryId')
  const loanAccountId = watch('loanAccountId')
  const amount = watch('amount')
  const [share, setShare] = useState<ShareValue>(EMPTY_SHARE)
  const [shareSubmitError, setShareSubmitError] = useState<string | null>(null)
  const linkedEmi = initial
    ? !!initial.loanAccountId
    : type === 'EXPENSE' && kind === 'EMI' && !!loanAccountId
  // Sharing is for payments only, and never for an EMI tied to a loan (that's the household's own debt).
  const canShare = !initial && type === 'EXPENSE' && !(kind === 'EMI' && !!loanAccountId)
  const paidByPerson = canShare && share.mode === 'PAID_BY'
  useEffect(() => {
    if (!canShare && share.mode !== 'JUST_US') setShare(EMPTY_SHARE)
  }, [canShare, share.mode])

  // type has no bearing on kind until it's EXPENSE — clear a stale kind when switching to
  // INCOME (the API rejects a kind on INCOME items), and default a freshly-revealed Kind
  // selector to Other rather than leaving it unset.
  useEffect(() => {
    if (type === 'INCOME') {
      if (kind) setValue('kind', undefined)
    } else if (!kind) {
      setValue('kind', 'OTHER')
    }
    if (kind !== 'EMI' && loanAccountId) setValue('loanAccountId', '')
  }, [type, kind, loanAccountId, setValue])

  // A pre-filled loan that isn't one of the household's (a stale link) isn't an EMI to anything.
  const prefilledLoan = defaults?.loanAccountId
  useEffect(() => {
    if (initial || !prefilledLoan || !accounts) return
    if (accounts.accounts.some((a) => a.type === 'LOAN' && a.id === prefilledLoan)) return
    setValue('loanAccountId', '')
    setValue('kind', 'OTHER')
  }, [initial, prefilledLoan, accounts, setValue])

  // The category dropdown is filtered to the current type — switching type can leave a
  // stale selection pointing at a category of the other type (the API rejects the mismatch).
  useEffect(() => {
    if (!categoryId) return
    const selected = categories?.categories.find((c) => c.id === categoryId)
    if (selected && selected.type !== type) setValue('categoryId', '')
  }, [type, categoryId, categories, setValue])

  const templates =
    kind === 'BILL' ? BILL_TEMPLATES : kind === 'SUBSCRIPTION' ? SUBSCRIPTION_TEMPLATES : null
  const iconOptions =
    kind === 'BILL'
      ? BILL_ICON_OPTIONS
      : kind === 'SUBSCRIPTION'
        ? SUBSCRIPTION_ICON_OPTIONS
        : CATEGORY_ICON_OPTIONS

  function applyTemplate(template: { name: string; icon: string }) {
    setValue('name', template.name)
    setValue('icon', template.icon)
  }

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => {
      // Only name, icon, and notes can change after creation — the API rejects
      // everything else. Cancel and add a new item to change anything else.
      if (initial) {
        return updateRecurring(initial.id, {
          name: values.name,
          icon: values.icon || undefined,
          notes: values.notes || undefined,
          amountVaries: values.amountVaries ?? false,
        })
      }
      return createRecurring({
        name: values.name,
        type: values.type,
        amount: values.amount,
        frequency: values.frequency,
        nextDueDate: values.nextDueDate,
        categoryId: values.categoryId || undefined,
        financialAccountId: paidByPerson ? undefined : values.financialAccountId || undefined,
        loanAccountId:
          values.type === 'EXPENSE' && values.kind === 'EMI'
            ? values.loanAccountId || undefined
            : undefined,
        notes: values.notes || undefined,
        kind: values.type === 'EXPENSE' ? values.kind : undefined,
        icon: values.icon || undefined,
        ...(values.amountVaries && !linkedEmi && { amountVaries: true }),
        ...(canShare ? sharePayload(share) : {}),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-6">
        <h2
          className="text-[24px] font-semibold text-[var(--fl-heading)] mb-5"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          {initial ? 'Edit Recurring Item' : 'Add Recurring Item'}
        </h2>
        <form
          onSubmit={handleSubmit((v) => {
            const err = canShare ? shareError(share, v.amount) : null
            if (err) return setShareSubmitError(err)
            saveMutation.mutate(v)
          })}
          className="space-y-4"
        >
          {!initial && type === 'EXPENSE' && (
            <div>
              <label
                htmlFor="kind"
                className="block text-sm font-medium text-[var(--fl-text-1)] mb-1"
              >
                Kind
              </label>
              <Controller
                control={control}
                name="kind"
                render={({ field }) => (
                  <Select
                    id="kind"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {KIND_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.kind && (
                <p className="text-xs text-[var(--fl-error)] mt-1">{errors.kind.message}</p>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-[var(--fl-text-1)] mb-1"
            >
              Name
            </label>
            <Input id="name" {...register('name')} placeholder="e.g. Netflix, Salary" />
            {errors.name && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Everything below shapes the transactions this item creates — fixed at
              creation. Editing an existing item shows it read-only instead; cancel the
              item and add a new one to change it. */}
          {initial && (
            <div className="rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface-subtle)] p-3 space-y-1.5">
              <SummaryRow label="Type" value={initial.type === 'EXPENSE' ? 'Payment' : 'Income'} />
              <SummaryRow label="Amount" value={format(initial.amount)} />
              <SummaryRow label="Frequency" value={FREQUENCY_LABELS[initial.frequency]} />
              <SummaryRow label="Next Due Date" value={formatDate(initial.nextDueDate)} />
              {initial.type === 'EXPENSE' && initial.kind && (
                <SummaryRow label="Kind" value={KIND_LABELS[initial.kind]} />
              )}
              <SummaryRow label="Category" value={initial.categoryName ?? 'None'} />
              <SummaryRow label="Paid from" value={initial.financialAccountName ?? 'None'} />
              {initial.loanAccountName && (
                <SummaryRow label="Loan" value={initial.loanAccountName} />
              )}
              {initial.paidByContactName && (
                <SummaryRow label="Paid by" value={initial.paidByContactName} />
              )}
              {initial.splits && initial.splits.length > 0 && (
                <SummaryRow
                  label="Split with"
                  value={initial.splits
                    .map((s) => `${s.contactName ?? 'Someone'} ${format(s.amount)}`)
                    .join(' · ')}
                />
              )}
              <p className="text-xs text-[var(--fl-text-2)] pt-1">
                To change these, cancel this item and add a new one.
              </p>
            </div>
          )}

          {!initial && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
                  Type
                </label>
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      aria-label="Type"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      <option value="EXPENSE">Payment</option>
                      <option value="INCOME">Income</option>
                    </Select>
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor="frequency"
                  className="block text-sm font-medium text-[var(--fl-text-1)] mb-1"
                >
                  Frequency
                </label>
                <Controller
                  control={control}
                  name="frequency"
                  render={({ field }) => (
                    <Select
                      id="frequency"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="HALF_YEARLY">Half-yearly</option>
                      <option value="YEARLY">Yearly</option>
                    </Select>
                  )}
                />
              </div>
            </div>
          )}

          {!initial && templates && (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick-add templates">
              {templates.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="flex items-center gap-1.5 rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface-subtle)] px-2.5 py-1 text-xs text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)] transition-colors"
                >
                  <CategoryIcon icon={t.icon} size={11} />
                  {t.name}
                </button>
              ))}
            </div>
          )}

          {type === 'EXPENSE' && (
            <div>
              <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
                Icon (optional)
              </label>
              <Controller
                control={control}
                name="icon"
                render={({ field }) => (
                  <IconPicker
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    options={iconOptions}
                  />
                )}
              />
            </div>
          )}

          {!initial && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
                  {paidByPerson ? 'Your share' : 'Amount'} ({symbol})
                </label>
                <Input {...register('amount')} type="number" step="0.01" placeholder="0.00" />
                {errors.amount && (
                  <p className="text-xs text-[var(--fl-error)] mt-1">{errors.amount.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
                  Next Due Date
                </label>
                <Controller
                  control={control}
                  name="nextDueDate"
                  render={({ field }) => (
                    <DatePicker value={field.value ?? ''} onChange={field.onChange} defaultToday />
                  )}
                />
                {errors.nextDueDate && (
                  <p className="text-xs text-[var(--fl-error)] mt-1">
                    {errors.nextDueDate.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {!initial && (
            <div>
              <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
                Category (optional)
              </label>
              <Controller
                control={control}
                name="categoryId"
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    searchable
                  >
                    <option value="">None</option>
                    {categories?.categories
                      .filter((c) => c.type === type)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </Select>
                )}
              />
            </div>
          )}

          {!initial && !paidByPerson && (
            <div>
              <label
                htmlFor="financialAccountId"
                className="block text-sm font-medium text-[var(--fl-text-1)] mb-1"
              >
                Paid from (optional)
              </label>
              <Controller
                control={control}
                name="financialAccountId"
                render={({ field }) => (
                  <Select
                    id="financialAccountId"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    disabled={!!defaultFinancialAccountId}
                    className={defaultFinancialAccountId ? 'opacity-60 cursor-not-allowed' : ''}
                  >
                    <option value="">None</option>
                    {accounts?.accounts
                      .filter((a) => a.type !== 'LOAN')
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                  </Select>
                )}
              />
            </div>
          )}

          {!initial && type === 'EXPENSE' && kind === 'EMI' && (
            <div>
              <label
                htmlFor="loanAccountId"
                className="block text-sm font-medium text-[var(--fl-text-1)] mb-1"
              >
                Loan (optional)
              </label>
              <Controller
                control={control}
                name="loanAccountId"
                render={({ field }) => (
                  <Select id="loanAccountId" value={field.value ?? ''} onChange={field.onChange}>
                    <option value="">Not linked</option>
                    {accounts?.accounts
                      .filter((a) => a.type === 'LOAN')
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                  </Select>
                )}
              />
              <p className="text-xs text-[var(--fl-text-2)] mt-1">
                {loanAccountId
                  ? 'Mark as Paid will ask how much was principal and interest, and update the loan.'
                  : 'Link the loan to update its outstanding when you mark this paid.'}
              </p>
            </div>
          )}

          {canShare && (
            <div>
              <RecurringShareSection
                totalRupees={Number(amount) || 0}
                value={share}
                onChange={(v) => {
                  setShareSubmitError(null)
                  setShare(v)
                }}
              />
              {shareSubmitError && (
                <p className="mt-1 text-xs text-[var(--fl-error)]">{shareSubmitError}</p>
              )}
            </div>
          )}

          {!linkedEmi && (
            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[var(--fl-text-1)]">
              <Checkbox className="mt-0.5" {...register('amountVaries')} />
              <span>
                The amount changes each time
                <span className="block text-xs text-[var(--fl-text-2)]">
                  For bills like water or electricity. The amount is an estimate, and after each
                  payment it becomes the average of the last 3.
                </span>
              </span>
            </label>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
              Notes (optional)
            </label>
            <Textarea {...register('notes')} rows={2} className="resize-none" />
          </div>

          {saveMutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(saveMutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || saveMutation.isPending}
              className="flex-1 font-semibold"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────

const VALID_TABS: Tab[] = ['payments', 'income', 'cancelled']

const PREFILL_PARAMS = ['add', 'kind', 'frequency', 'name', 'loan']
const PREFILL_KINDS: RecurringKind[] = ['BILL', 'EMI', 'SUBSCRIPTION', 'OTHER']
const PREFILL_FREQUENCIES: Frequency[] = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'YEARLY']

function readPrefill(params: URLSearchParams): {
  type: RecurringType
  defaults: NonNullable<ComponentProps<typeof RecurringModal>['defaults']>
} | null {
  const add = params.get('add')
  if (add !== 'EXPENSE' && add !== 'INCOME') return null
  const defaults: NonNullable<ComponentProps<typeof RecurringModal>['defaults']> = {}
  const kind = params.get('kind') as RecurringKind
  const frequency = params.get('frequency') as Frequency
  const name = params.get('name')?.trim().slice(0, 100)
  const loan = params.get('loan')
  if (add === 'EXPENSE') {
    if (PREFILL_KINDS.includes(kind)) defaults.kind = kind
    if (loan) {
      defaults.kind = 'EMI'
      defaults.loanAccountId = loan
    }
  }
  if (PREFILL_FREQUENCIES.includes(frequency)) defaults.frequency = frequency
  if (name) defaults.name = name
  return { type: add, defaults }
}

export default function RecurringPage() {
  const isReadOnly = useIsReadOnly()
  const readOnlyKnown = useReadOnlyKnown()
  const wide = useMediaQuery(DESKTOP_QUERY)
  // Read once on mount (e.g. Analytics nudges link to ?tab=income&add=INCOME); the tab strip's
  // own clicks drive it from here on.
  const [searchParams, setSearchParams] = useSearchParams()
  const [prefill, setPrefill] = useState(() => readPrefill(searchParams))
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    // An add form opens on its own tab, whatever ?tab says.
    if (prefill) return prefill.type === 'INCOME' ? 'income' : 'payments'
    const tab = searchParams.get('tab')
    return VALID_TABS.includes(tab as Tab) ? (tab as Tab) : 'payments'
  })
  // Read-only is only known once its queries load, so the add form is gated at render rather than here.
  const [showModal, setShowModal] = useState(() => !!prefill)
  // Only a form a link opened waits for read-only to be known: a click on Add comes after the page loads.
  const [openedByLink] = useState(showModal)
  useEffect(() => {
    if (!PREFILL_PARAMS.some((k) => searchParams.has(k))) return
    const next = new URLSearchParams(searchParams)
    PREFILL_PARAMS.forEach((k) => next.delete(k))
    setSearchParams(next, { replace: true })
    // Once on mount: later param changes must not reopen the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [editItem, setEditItem] = useState<RecurringItem | undefined>()
  const [openSnapshot, setOpenSnapshot] = useState<RecurringItem | null>(null)
  // The open item stays in state, never in the URL (no ids in URLs); a history entry lets the
  // phone's Back close it.
  const pushedRef = useRef(false)
  useEffect(() => {
    if (!openSnapshot) return
    const onPop = () => {
      pushedRef.current = false
      setOpenSnapshot(null)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [openSnapshot])

  const activeQuery = useQuery({
    queryKey: ['recurring', 'active'],
    queryFn: () => listRecurring('active'),
  })
  const dueSoonQuery = useQuery({
    queryKey: ['recurring', 'due_soon'],
    queryFn: () => listRecurring('due_soon'),
  })
  const cancelledQuery = useQuery({
    queryKey: ['recurring', 'cancelled'],
    queryFn: () => listRecurring('cancelled'),
    enabled: activeTab === 'cancelled',
  })

  const active = activeQuery.data?.items ?? []
  const payments = active.filter((i) => i.type === 'EXPENSE')
  const income = active.filter((i) => i.type === 'INCOME')
  const today = todayIso()
  const nextUp = active.find((i) => i.nextDueDate >= today) ?? null
  const tabQuery = activeTab === 'cancelled' ? cancelledQuery : activeQuery
  const items =
    activeTab === 'payments'
      ? payments
      : activeTab === 'income'
        ? income
        : (cancelledQuery.data?.items ?? [])

  const defaultModalType: RecurringType =
    prefill && !editItem ? prefill.type : activeTab === 'income' ? 'INCOME' : 'EXPENSE'

  function openAdd() {
    setEditItem(undefined)
    setShowModal(true)
  }

  function openEdit(item: RecurringItem) {
    setEditItem(item)
    setShowModal(true)
  }

  function openItem(item: RecurringItem) {
    window.history.pushState({ ...window.history.state, recurringDetail: true }, '')
    pushedRef.current = true
    setOpenSnapshot(item)
  }

  function closeItem() {
    setOpenSnapshot(null)
    if (pushedRef.current) {
      pushedRef.current = false
      window.history.back()
    }
  }

  // Looked up from the loaded lists so it stays fresh after an action; the snapshot covers an
  // item that just left them (cancelled while Payments is showing).
  const openItemData = openSnapshot
    ? ([...active, ...(cancelledQuery.data?.items ?? []), ...(dueSoonQuery.data?.items ?? [])].find(
        (i) => i.id === openSnapshot.id
      ) ?? openSnapshot)
    : null

  const tabs: { id: Tab; label: string; count?: number }[] = [
    {
      id: 'payments',
      label: 'Payments',
      count: activeQuery.isSuccess ? payments.length : undefined,
    },
    { id: 'income', label: 'Income', count: activeQuery.isSuccess ? income.length : undefined },
    {
      id: 'cancelled',
      label: 'Cancelled',
      count: cancelledQuery.isSuccess ? cancelledQuery.data.items.length : undefined,
    },
  ]

  return (
    <div className="px-4 py-5 md:p-10">
      <header className="mb-4 flex items-center justify-between gap-4 md:mb-5">
        <h1 className="font-serif text-[22px] font-semibold text-[var(--fl-heading)] md:text-[30px]">
          Recurring
        </h1>
        {!isReadOnly && (
          <Button
            onClick={openAdd}
            className={`h-auto gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm active:scale-95 shrink-0 ${
              tabQuery.isSuccess && items.length === 0 && activeTab !== 'cancelled'
                ? 'motion-safe:animate-attention-pulse'
                : ''
            }`}
          >
            <Plus size={16} /> Add
          </Button>
        )}
      </header>

      {activeQuery.data && (
        <RecurringHero
          summary={activeQuery.data.summary}
          paymentCount={payments.length}
          nextUp={nextUp}
        />
      )}

      <DueThisWeekStrip items={dueSoonQuery.data?.items ?? []} onOpen={openItem} />

      <div role="tablist" className="mt-6 mb-1 flex gap-1 border-b border-[var(--fl-border)]">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            count={tab.count}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </div>

      {tabQuery.isLoading && <p className="mt-4 text-sm text-[var(--fl-text-2)]">Loading…</p>}
      {tabQuery.isError && (
        <p className="mt-4 text-sm text-[var(--fl-error)]">Failed to load recurring items.</p>
      )}

      {tabQuery.isSuccess && items.length === 0 && (
        <div className="mt-4">
          {activeTab === 'cancelled' && (
            <EmptyState
              icon={CalendarOff}
              title="No cancelled items"
              description="Anything you stop tracking stays here for reference."
            />
          )}
          {activeTab === 'payments' && (
            <EmptyState
              icon={CalendarClock}
              title="Set up a recurring payment"
              description="Subscriptions, EMIs, rent — anything that leaves your accounts on a schedule, so nothing catches you off guard."
            />
          )}
          {activeTab === 'income' && (
            <EmptyState
              icon={CalendarClock}
              title="Set up recurring income"
              description="Salary, rental yield, or any regular inflow — track it so your month starts with a clear picture."
            />
          )}
        </div>
      )}

      {tabQuery.isSuccess && items.length > 0 && (
        <>
          {activeTab === 'payments' ? (
            <KindGroups items={payments} wide={wide} onOpen={openItem} onEdit={openEdit} />
          ) : (
            <RowPanel items={items} onOpen={openItem} onEdit={openEdit} className="mt-4" />
          )}
        </>
      )}

      {openItemData && (
        <RecurringDetail item={openItemData} onClose={closeItem} onEdit={openEdit} />
      )}

      {((showModal && !isReadOnly && (readOnlyKnown || !openedByLink)) || editItem) && (
        <RecurringModal
          initial={editItem}
          defaultType={defaultModalType}
          defaults={editItem ? undefined : prefill?.defaults}
          onClose={() => {
            setShowModal(false)
            setEditItem(undefined)
            setPrefill(null)
          }}
        />
      )}
    </div>
  )
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function RowPanel({
  items,
  onOpen,
  onEdit,
  className,
}: {
  items: RecurringItem[]
  onOpen: (item: RecurringItem) => void
  onEdit: (item: RecurringItem) => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 shadow-[var(--card-shadow-sm)]',
        className
      )}
    >
      {items.map((item) => (
        <RecurringRow key={item.id} item={item} onOpen={onOpen} onEdit={onEdit} />
      ))}
    </div>
  )
}

function KindGroups({
  items,
  wide,
  onOpen,
  onEdit,
}: {
  items: RecurringItem[]
  wide: boolean
  onOpen: (item: RecurringItem) => void
  onEdit: (item: RecurringItem) => void
}) {
  const { format } = useFormatCurrency()
  // A legacy row's kind is always resolved to OTHER by the backend, so every EXPENSE item lands
  // in exactly one section.
  const groups = KIND_SECTIONS.map(({ kind, title }) => ({
    kind,
    title,
    items: items.filter((i) => (i.kind ?? 'OTHER') === kind),
  })).filter((g) => g.items.length > 0)

  const group = (g: (typeof groups)[number]) => (
    <section key={g.kind} aria-label={g.title}>
      <div className="mt-5 mb-2 flex items-baseline justify-between px-0.5">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
          {g.title} · {g.items.length}
        </h2>
        <span className="text-xs tabular-nums text-[var(--fl-text-2)]">
          {format(g.items.reduce((s, i) => s + i.monthlyEquivalent, 0))}/mo
        </span>
      </div>
      <RowPanel items={g.items} onOpen={onOpen} onEdit={onEdit} />
    </section>
  )

  if (!wide || groups.length < 2) return <div>{groups.map(group)}</div>
  return (
    <div className="grid grid-cols-2 items-start gap-x-5">
      <div>{groups.filter((_, i) => i % 2 === 0).map(group)}</div>
      <div>{groups.filter((_, i) => i % 2 === 1).map(group)}</div>
    </div>
  )
}
